import { db } from './firebase';
import {
  collection,
  query,
  where,
  limit,
  getDocs,
  Timestamp,
} from 'firebase/firestore';

export interface TrendingItem {
  id: string;
  title: string;
  likes: number;
  createdAt: string;
}

export interface TrendingCreator {
  id: string;
  name: string;
  followers: number;
  growth: number;
  avatar?: string;
}

export interface LocationTrend {
  location: string;
  postCount: number;
  engagement: number;
}

export const TrendingService = {
  async fetchTrendingPosts(): Promise<TrendingItem[]> {
    try {
      const now = Timestamp.now();
      const fortyEightHoursAgo = new Timestamp(now.seconds - 48 * 60 * 60, 0);

      const postsRef = collection(db, 'posts');
      const q = query(
        postsRef,
        where('createdAt', '>=', fortyEightHoursAgo),
        limit(100)
      );

      const snapshot = await getDocs(q);
      const posts = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title || 'Untitled',
          likes: data.likes || 0,
          createdAt: data.createdAt instanceof Timestamp
            ? data.createdAt.toDate().toISOString()
            : data.createdAt || new Date().toISOString(),
        };
      });

      return posts
        .sort((a, b) => b.likes - a.likes)
        .slice(0, 5);
    } catch (err) {
      console.error('[TrendingService] Error fetching posts:', err);
      return [];
    }
  },

  async fetchTrendingEvents(): Promise<TrendingItem[]> {
    try {
      const now = Timestamp.now();
      const fortyEightHoursAgo = new Timestamp(now.seconds - 48 * 60 * 60, 0);

      const eventsRef = collection(db, 'events');
      const q = query(
        eventsRef,
        where('createdAt', '>=', fortyEightHoursAgo),
        limit(100)
      );

      const snapshot = await getDocs(q);
      const events = snapshot.docs.map((doc) => {
        const data = doc.data();
        // Calculate engagement score
        const likes = data.likes || 0;
        const comments = data.comments || 0;
        const shares = data.shares || 0;
        const views = data.views || 1;
        const engagement = ((likes + comments * 2 + shares * 3) / views) * 100;
        
        return {
          id: doc.id,
          title: data.title || 'Untitled Event',
          likes: Math.round(engagement), // Use engagement score
          createdAt: data.createdAt instanceof Timestamp
            ? data.createdAt.toDate().toISOString()
            : data.createdAt || new Date().toISOString(),
        };
      });

      return events
        .sort((a, b) => b.likes - a.likes)
        .slice(0, 5);
    } catch (err) {
      console.error('[TrendingService] Error fetching events:', err);
      return [];
    }
  },

  async fetchTrendingCreators(): Promise<TrendingCreator[]> {
    try {
      const usersRef = collection(db, 'users');
      const q = query(
        usersRef,
        where('isCreator', '==', true),
        limit(50)
      );

      const snapshot = await getDocs(q);
      const creators = snapshot.docs.map((doc) => {
        const data = doc.data();
        const followers = data.followers || 0;
        const prevFollowers = data.prevFollowers || followers;
        const growth = prevFollowers > 0 
          ? Math.round(((followers - prevFollowers) / prevFollowers) * 100)
          : 0;
        
        return {
          id: doc.id,
          name: data.displayName || data.username || 'Unknown',
          followers,
          growth,
          avatar: data.avatar || data.photoURL,
        };
      });

      // Sort by growth rate and take top 5 rising creators
      return creators
        .sort((a, b) => b.growth - a.growth)
        .slice(0, 5);
    } catch (err) {
      console.error('[TrendingService] Error fetching creators:', err);
      return [];
    }
  },

  async fetchLocationBasedTrending(userLocation?: string): Promise<LocationTrend[]> {
    try {
      if (!userLocation) {
        return [];
      }

      const postsRef = collection(db, 'posts');
      const q = query(
        postsRef,
        where('location', '>=', userLocation),
        where('location', '<=', userLocation + '\uf8ff'),
        limit(50)
      );

      const snapshot = await getDocs(q);
      const locationMap = new Map<string, { posts: number; engagement: number }>();

      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        const location = data.location || 'Unknown';
        const likes = data.likes || 0;
        const comments = data.comments || 0;
        const shares = data.shares || 0;
        
        const current = locationMap.get(location) || { posts: 0, engagement: 0 };
        locationMap.set(location, {
          posts: current.posts + 1,
          engagement: current.engagement + likes + comments + shares,
        });
      });

      const trends: LocationTrend[] = Array.from(locationMap.entries())
        .map(([location, data]) => ({
          location,
          postCount: data.posts,
          engagement: data.engagement,
        }))
        .sort((a, b) => b.engagement - a.engagement)
        .slice(0, 5);

      return trends;
    } catch (err) {
      console.error('[TrendingService] Error fetching location trends:', err);
      return [];
    }
  },
};
